import React from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import Auth from '@aws-amplify/auth';
import Amplify from '@aws-amplify/core';
import { ApolloClient, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { v4 as uuid } from 'uuid';
import btoa from 'btoa';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import WebSocketServer from 'ws';
import { AuthProvider } from '../context/authContext';
import { AnalyticsProvider } from '../context/analyticsContext';
import { EventProvider } from '../context/EventContext';
import { EnhancedAnalyticsProvider } from '../context/enhancedAnalyticsContext';
import { CurrencyProvider } from '../context/CurrencyContext';
import { LogProvider } from '../context/logContext';

const graphqlPrinter = require('graphql/language/printer');

const theme = extendTheme({
  colors: {
    brand: {
      50: '#EBF8FF',
      100: '#BEE3F8',
      200: '#90CDF4',
      300: '#63B3ED',
      400: '#4299E1',
      500: '#3182CE',
      600: '#0F517F', // '#2B6CB0',
      700: '#2C5282',
      800: '#2A4365',
      900: '#1A365D'
    },
    text: {
      50: '#F7FAFC',
      100: '#EDF2F7',
      200: '#E2E8F0',
      300: '#CBD5E0',
      400: '#A0AEC0',
      500: '#718096',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#171923'
    }
  }
});
const config = {
  Auth: {
    region: process.env.REGION,
    userPoolId: process.env.USER_POOL_ID,
    userPoolWebClientId: process.env.USER_POOL_CLIENT_ID
  }
};
Amplify.configure(config);
Auth.configure(config);
// Auth.currentAuthenticatedUser()
const APPSYNC_URL = process.env.APP_SYNC_API_URL as string;
const APPSYNC_KEY = process.env.APP_SYNC_API_KEY as string;
const HOST = APPSYNC_URL.replace('https://', '').replace('/graphql', '');
const WSS_URL = APPSYNC_URL.replace('https', 'wss').replace('appsync-api', 'appsync-realtime-api');

// eslint-disable-next-line
const api_header = {
  host: HOST,
  'x-api-key': APPSYNC_KEY
};
// eslint-disable-next-line
const header_encode = (obj) => btoa(JSON.stringify(obj));
// eslint-disable-next-line
const connection_url = WSS_URL + '?header=' + header_encode(api_header) + '&payload=' + header_encode({});
const getWebsocket: any = () => {
  if (typeof WebSocket === 'undefined') {
    return WebSocketServer;
  }

  return WebSocket;
};

// @ts-ignore
class UUIDOperationIdSubscriptionClient extends SubscriptionClient {
  // eslint-disable-next-line
  generateOperationId() {
    getWebsocket();
    // AppSync recommends using UUIDs for Subscription IDs but SubscriptionClient uses an incrementing number
    return uuid();
  }

  processReceivedData(receivedData) {
    try {
      const parsedMessage = JSON.parse(receivedData);
      if (parsedMessage?.type === 'start_ack') return; // sent by AppSync but meaningless to us
    } catch (e) {
      throw new Error(`Message must be JSON-parsable. Got: ${receivedData}`);
    }
    // @ts-ignore
    super.processReceivedData(receivedData);
  }
}

const createAppSyncGraphQLOperationAdapter = () => ({
  applyMiddleware: async (options, next) => {
    // eslint-disable-next-line
    options.data = JSON.stringify({
      query: typeof options.query === 'string' ? options.query : graphqlPrinter.print(options.query),
      variables: options.variables
    });
    // eslint-disable-next-line no-param-reassign
    options.extensions = { authorization: api_header };
    // AppSync does not care about these properties
    // eslint-disable-next-line
    delete options.operationName;
    // eslint-disable-next-line
    delete options.variables;
    // Not deleting "query" property as SubscriptionClient validation requires it
    next();
  }
});
// WebSocketLink
const wsLink = new WebSocketLink(
  new UUIDOperationIdSubscriptionClient(
    connection_url,
    {
      timeout: 5 * 60 * 1000,
      reconnect: true,
      lazy: true,
      // eslint-disable-next-line no-console
      connectionCallback: (err) => console.log('connectionCallback', err ? 'ERR' : 'OK', err || '')
    },
    getWebsocket()
  ).use([createAppSyncGraphQLOperationAdapter()])
);
const httpLink = createHttpLink({
  uri: APPSYNC_URL
});
// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);
const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  try {
    // return the headers to the context so httpLink can read them
    // console.log('Curren TOKEN:::', (await Auth.currentSession()).getIdToken().getJwtToken());
    const JwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
    if (JwtToken) {
      return {
        headers: {
          ...headers,
          authorization: JwtToken
        }
      };
    }
  } catch (err) {
    console.error('Auth session token error', err);
    return {
      headers: {
        ...headers,
        'x-api-key': APPSYNC_KEY
      }
    };
  }
  return {
    headers: {
      ...headers
    }
  };
});
const client = new ApolloClient({
  link: authLink.concat(splitLink),
  cache: new InMemoryCache()
});

function MyApp({ Component, pageProps }: { Component: any; pageProps: any }) {
  return (
    <LogProvider>
      <ChakraProvider theme={theme}>
        <ApolloProvider client={client as any}>
          <AuthProvider>
            <CurrencyProvider>
              <EnhancedAnalyticsProvider>
                <EventProvider>
                  <AnalyticsProvider>
                    <Component {...pageProps} />
                  </AnalyticsProvider>
                </EventProvider>
              </EnhancedAnalyticsProvider>
            </CurrencyProvider>
          </AuthProvider>
        </ApolloProvider>
      </ChakraProvider>
    </LogProvider>
  );
}

export default MyApp;
